import { Box, Link, Tooltip, useColorModeValue } from '@chakra-ui/react'
import type { SystemStyleObject } from '@chakra-ui/react'
import DOMPurify from 'dompurify'
import React from 'react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  convertYoutubeUrlToEmbed,
  insertInlineCitations,
  isYoutubeLink,
  resolveImageTags,
} from '../../core/parsers/markdownParser'
import { ImageTag, Reference } from '../../types/chatTypes'
import { LinkChip } from '../LinkChip'
import AssetImage from './AssetImage'

// Same-origin path of an extracted document asset served by the unified
// endpoint, e.g. `/api/assets/{conversation|knowledge}/...` — Bearer-authed, so
// it needs the authed fetch path (AssetImage) rather than a bare `<img src>`.
const ASSET_IMAGE_SRC = /^\/?api\/assets\//

// react-markdown's default url sanitizer only passes http(s)/mailto/xmpp, which
// would blank the href of a OneNote citation (`onenote:https://…`, the protocol
// that opens the page in the OneNote app). Allow that one wrapper through; the
// url underneath is still sanitized by the default transform.
const ONENOTE_PROTOCOL = 'onenote:'

const urlTransform = (url: string): string =>
  url.toLowerCase().startsWith(ONENOTE_PROTOCOL)
    ? ONENOTE_PROTOCOL + defaultUrlTransform(url.slice(ONENOTE_PROTOCOL.length))
    : defaultUrlTransform(url)

interface AnswerTextProps {
  htmlContent?: string
  children?: React.ReactNode
  markdownContent?: string
  references?: Reference[]
  images?: ImageTag[]
}

const sanitizeHtml = (html: string) => {
  // Configure DOMPurify with allowed tags and attributes
  const config = {
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'p', 'br', 'ul', 'ol', 'li', 'a', 'img', 'iframe'],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'src',
      'alt',
      'width',
      'height',
      'frameborder',
      'allowfullscreen',
      'allow',
    ],
    FORCE_REL: 'noopener noreferrer',
    FORCE_TARGET: '_blank',
  }

  return DOMPurify.sanitize(html, config)
}

const AnswerText: React.FC<AnswerTextProps> = ({
  htmlContent,
  children,
  markdownContent,
  references,
  images,
}) => {
  // Resolve inline [S#] citation markers into numbered markdown links, then
  // resolve inline [IMG#] image markers into asset images. Both maps stream in
  // before the answer tokens, so markers resolve live; before then citation
  // markers are stripped and unresolved image tags are hidden.
  const renderedMarkdown = React.useMemo(
    () => resolveImageTags(insertInlineCitations(markdownContent ?? '', references), images),
    [markdownContent, references, images],
  )

  // url -> reference, to tell citation links (numbered, href is a known
  // reference) apart from ordinary links and to label their hover tooltip.
  const citationByUrl = React.useMemo(
    () => new Map((references ?? []).filter(ref => ref.url).map(ref => [ref.url, ref])),
    [references],
  )

  // Color mode values
  const textColor = useColorModeValue('gray.900', 'gray.100')
  const citationColor = useColorModeValue('blue.600', 'blue.300')
  const codeBg = useColorModeValue('gray.50', 'gray.700')
  const cardBg = useColorModeValue('rgb(249, 249, 249)', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const tableHeaderBg = useColorModeValue('gray.100', 'gray.700')
  const tableStripeBg = useColorModeValue('gray.50', 'whiteAlpha.100')
  const linkColor = useColorModeValue('blue.500', 'blue.300')
  const linkHoverColor = useColorModeValue('blue.600', 'blue.400')

  // Shared table styling reused across html / markdown / fallback branches.
  // Soft borders, header bg, zebra rows, horizontal scroll for wide tables.
  const tableSx: SystemStyleObject = {
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      my: 3,
      fontSize: '0.9em',
      display: 'block',
      overflowX: 'auto',
    },
    '& thead th': {
      bg: tableHeaderBg,
      fontWeight: 600,
      textAlign: 'left',
    },
    '& tbody tr:nth-of-type(even)': {
      bg: tableStripeBg,
    },
    '& td, & th': {
      border: '1px solid',
      borderColor: borderColor,
      padding: '6px 10px',
    },
  }

  return (
    <>
      {htmlContent ? (
        <Box
          pr={2}
          borderRadius='0px 16px 16px 0px'
          color={textColor}
          fontSize='15px'
          fontWeight='400'
          lineHeight='1.2em'
          whiteSpace='pre-line'
          minWidth={['60%', '60%', '50%']}
          maxWidth={['90%', '90%', '85%']}
          display='flex'
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlContent) }}
          sx={{
            ...tableSx,
            '& ul': {
              marginLeft: '20px',
            },
          }}
        />
      ) : markdownContent ? (
        <Box
          pr={2}
          borderRadius='0px 16px 16px 0px'
          color={textColor}
          fontSize='15px'
          fontWeight='400'
          lineHeight='1.2em'
          minWidth={['60%', '60%', '50%']}
          maxWidth={['90%', '90%', '85%']}
          display='flex'
          flexDirection='column'
          sx={{
            '& pre': {
              bg: codeBg,
              p: 4,
              borderRadius: 'md',
              overflowX: 'auto',
            },
            '& code': {
              fontFamily: 'mono',
              fontSize: '14px',
            },
            '& p': {
              my: 2,
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              fontWeight: 'bold',
              my: 3,
            },
            '& ul, & ol': {
              ml: 6,
              my: 2,
            },
            '& li': {
              my: 2,
            },
            '& hr': {
              borderColor: textColor,
            },
            ...tableSx,
            '& blockquote': {
              borderLeft: '4px solid',
              borderColor: borderColor,
              pl: 4,
              my: 2,
            },
            '& a': {
              color: linkColor,
              textDecoration: 'underline',
              cursor: 'pointer',
              _hover: {
                color: linkHoverColor,
              },
            },
            '& iframe': {
              my: 2,
              maxWidth: '100%',
              border: 'none',
              borderRadius: 'md',
            },
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            urlTransform={urlTransform}
            components={{
              img: ({ src, alt }: React.ComponentProps<'img'>) => {
                // Document-asset images are Bearer-authed: fetch via the authed
                // client into an object URL. Anything else renders as a normal
                // image.
                if (typeof src === 'string' && ASSET_IMAGE_SRC.test(src)) {
                  return <AssetImage src={src} alt={alt} />
                }
                return <img src={src} alt={alt ?? ''} style={{ maxWidth: '100%' }} />
              },
              a: ({ children, ...props }: React.ComponentProps<'a'>) => {
                const href = props.href || ''

                // Handle YouTube links
                if (isYoutubeLink(href)) {
                  // Use dangerouslySetInnerHTML to render the iframe
                  return (
                    <span
                      dangerouslySetInnerHTML={{
                        __html: convertYoutubeUrlToEmbed(href),
                      }}
                    />
                  )
                }

                const linkText = React.Children.toArray(children)
                  .map(c => (typeof c === 'string' ? c : ''))
                  .join('')
                  .trim()

                // Citation links: numeric label whose href is a known
                // reference. Render as a compact `[n]` chip, title (+ page) on
                // hover.
                const citation = citationByUrl.get(href)
                if (/^\d+$/.test(linkText) && citation) {
                  const tooltipLabel = citation.page
                    ? `${citation.title} · p. ${citation.page}`
                    : citation.title
                  return (
                    <Tooltip label={tooltipLabel} openDelay={300} hasArrow>
                      <Link
                        href={href}
                        target='_blank'
                        rel='noopener noreferrer'
                        color={citationColor}
                        fontSize='0.85em'
                        fontWeight='500'
                        whiteSpace='nowrap'
                        textDecoration='none'
                        _hover={{ textDecoration: 'underline' }}
                      >
                        [{linkText}]
                      </Link>
                    </Tooltip>
                  )
                }

                // Use LinkChip for all external links (including SharePoint)
                return <LinkChip href={href} label={linkText || href} title={linkText || href} />
              },
            }}
          >
            {renderedMarkdown}
          </ReactMarkdown>
        </Box>
      ) : (
        <Box
          p={8}
          py={4}
          background={cardBg}
          borderRadius={8}
          boxShadow='0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)'
          fontSize='15px'
          fontWeight='400'
          lineHeight='22px'
          whiteSpace='pre-line'
          maxWidth='60%'
          display='flex'
          flexDirection='column'
          minWidth='500px'
          sx={{
            ...tableSx,
            '> p': {
              margin: 0,
            },
          }}
        >
          {children}
        </Box>
      )}
    </>
  )
}

export default React.memo(AnswerText)
